/**
 * scoreboardData — one shared read of the dashboard balance scoreboard.
 *
 * WHY THIS EXISTS
 * The scoreboard document (companies/{id}/summaries/balances) is maintained by
 * the maintainBalanceScoreboard Cloud Function and already contains everything
 * three separate panels need:
 *   • KPI cards          → fy[fyId].all / .branch[branchId]  (cash/bank/receivable/payable)
 *   • Money Position     → monthly[fyId]                     (per-month movement)
 *   • Receivable/Payable → fy[fyId] totals
 *
 * Without a shared loader each panel would fetch the same document, turning a
 * 1-read dashboard into a 3-read one and tripling the latency for no new data.
 * This module reads it ONCE per company+refresh and hands the same object to
 * every consumer.
 *
 * COST: 1 Firestore read per dashboard load (cached for DASHBOARD_TTL_MS).
 * The panels built on it are therefore effectively free.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCached } from './fyDataCache';
import { DASHBOARD_TTL_MS } from './dashboardHelper';
import { recordReads } from './readTelemetry';

/**
 * Raw scoreboard document, or null when it doesn't exist.
 * Keyed under the `dashRaw|` prefix so the dashboard Refresh button's existing
 * invalidation clears it along with everything else.
 */
export const getScoreboard = async (companyId, forceRefresh = false) => {
  if (!companyId) return null;
  return getCached(
    `dashRaw|${companyId}|scoreboard`,
    async () => {
      const snap = await getDoc(doc(db, 'companies', companyId, 'summaries', 'balances'));
      recordReads(companyId, 1);
      return snap.exists() ? snap.data() : null;
    },
    DASHBOARD_TTL_MS,
    forceRefresh
  );
};

/**
 * Is this scoreboard safe to display for the given financial year?
 *
 * Mirrors the rule KPICards uses: trust it only after a full baseline
 * recompute (`recomputedAt`), and only when this financial year actually has a
 * node. Before the backfill the document holds post-deploy deltas alone, which
 * would render as confidently wrong numbers.
 */
export const scoreboardUsable = (board, fyId) =>
  !!(board && board.recomputedAt && fyId && board.fy && board.fy[fyId]);

/**
 * Totals for a financial year, scoped to a branch when one is selected.
 * Returns null when the requested branch has no node — the caller should treat
 * that as "no data" rather than showing four zeros, which reads as real.
 */
export const scoreboardTotals = (board, fyId, branchId) => {
  const fyNode = board?.fy?.[fyId];
  if (!fyNode) return null;
  const bucket = branchId ? fyNode.branch?.[branchId] : (fyNode.all || {});
  if (!bucket) return null;
  return {
    cash: Number(bucket.cash) || 0,
    bank: Number(bucket.bank) || 0,
    receivable: Number(bucket.receivable) || 0,
    payable: Number(bucket.payable) || 0,
  };
};

/**
 * Month-by-month RUNNING balances for a financial year.
 *
 * `monthly` stores each month's MOVEMENT, not its closing balance, so a running
 * total is what turns it into a line you can read. Same accumulation the KPI
 * sparklines already do — kept here once instead of repeated per widget.
 *
 * Returns [{ key:'2026-07', label:'Jul', cash, bank, receivable, payable }, …]
 * oldest first, or [] when there is nothing to show.
 *
 * NOTE: `monthly` is company-wide — it carries no per-branch split. With a
 * branch selected the shape of the trend is still meaningful, but the values
 * are the whole company's. Callers should say so in the UI.
 */
export const scoreboardMonthly = (board, fyId, maxMonths = 8) => {
  const monthly = board?.monthly?.[fyId];
  if (!monthly) return [];
  const keys = Object.keys(monthly).filter(k => k && k !== 'unknown').sort();
  if (keys.length === 0) return [];

  const run = { cash: 0, bank: 0, receivable: 0, payable: 0 };
  const all = keys.map((k) => {
    const m = monthly[k] || {};
    run.cash += Number(m.cash) || 0;
    run.bank += Number(m.bank) || 0;
    run.receivable += Number(m.receivable) || 0;
    run.payable += Number(m.payable) || 0;
    const [y, mm] = k.split('-');
    const label = new Date(Number(y), Number(mm) - 1, 1)
      .toLocaleString('en', { month: 'short' });
    return { key: k, label, ...run };
  });

  // Keep the running total intact by slicing only the tail we display.
  return all.slice(-maxMonths);
};
